import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { canUseMarketingTools, isToolHiddenForRole } from '../config/marketingTools';
import { Calculator, FileText, Receipt, Megaphone, TrendingUp, ArrowLeft, Database, FolderOpen } from 'lucide-react';

// Optional photo for each tool tile. Put a file in public/tool-photos and set image to its path, e.g. '/tool-photos/cma.jpg'.
const TOOLS = [
  { title: 'Residence Pocket Listing Database', text: 'Search homes our sellers are thinking about selling by community, floor plan, pool, view and price.', Icon: Database, route: '/tools/pocket-listings' },
  { title: 'CMA Builder', text: 'Compare recent sold homes and estimate a price range for a listing.', Icon: TrendingUp, route: '/tools/cma', image: '/tool-photos/cma-builder.jpg' },
  { title: 'Monthly Payment Calculator', text: 'Estimate monthly payment with taxes, insurance, HOA, and PMI.', Icon: Calculator, route: '/tools/mortgage', image: '/tool-photos/mortgage-calculator.jpg' },
  { title: 'Seller Net Sheet', text: 'Estimate what a seller takes home after commissions and closing costs.', Icon: FileText, route: '/tools/net-sheet', image: '/tool-photos/seller-net-sheet.jpg' },
  { title: 'Closing Cost Estimate', text: 'Estimate Florida closing costs for a buyer or a seller, with a printable summary.', Icon: Receipt, route: '/tools/closing-costs', image: '/tool-photos/closing-costs.jpg' },
  { title: 'Seller Market Update', text: 'Build a branded market update for sellers from the latest local numbers.', Icon: Megaphone, route: '/tools/market-update', image: '/tool-photos/market-update.jpg' },
];
// Shown only to Marketing Managers and Admins (route and server also check the role).
const MARKETING_TOOL = { title: 'Marketing Tools', text: 'Create a Canva folder for a listing with template copies. Eagle Eye photos and invoices are handled automatically.', Icon: FolderOpen, route: '/marketing-tools' };

const styles = {
  page: { minHeight: '100vh', background: '#f5f6f8', padding: '24px 16px', boxSizing: 'border-box' },
  wrap: { maxWidth: 1100, margin: '0 auto' },
  back: { display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: '#8C6D1F', fontWeight: 600, cursor: 'pointer', padding: 0, marginBottom: 14 },
  hero: {
    borderRadius: 18,
    padding: '28px 24px 24px',
    marginBottom: 20,
    background: '#ffffff',
    border: '1px solid #e6e1d3',
  },
  h1: { fontSize: 30, fontWeight: 800, color: '#1a1a1a', margin: '0 0 6px', letterSpacing: '-0.01em' },
  muted: { color: '#6b6b6b', fontSize: 15, margin: '0', lineHeight: 1.5 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 20 },
  tile: { background: '#111111', border: '1px solid #2a2a2a', borderRadius: 14, overflow: 'hidden', textAlign: 'left', padding: 0, cursor: 'pointer', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', display: 'flex', flexDirection: 'column', fontFamily: 'inherit' },
  photo: { height: 110, background: 'linear-gradient(135deg, #1a1a1a 0%, #2b2b2b 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  iconWrap: { width: 56, height: 56, borderRadius: 14, background: '#000000', border: '1px solid #D4AF37', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  body: { padding: '16px 20px 22px' },
  title: { fontSize: 19, fontWeight: 700, margin: '0 0 6px', color: '#FFFFFF' },
  text: { fontSize: 14, color: '#bdbdbd', margin: 0, lineHeight: 1.5 },
};

const CSS = `
  .tools-tile { transition: transform 0.18s ease, box-shadow 0.18s ease; }
  .tools-tile:hover { transform: translateY(-3px); box-shadow: 0 12px 26px rgba(0,0,0,0.35); }
  .tools-tile:focus-visible { outline: 3px solid #D4AF37; outline-offset: 3px; }
`;

export default function ToolsHub() {
  const navigate = useNavigate();
  const { userRole } = useAuth();
  const visible = TOOLS.filter((t) => !isToolHiddenForRole(userRole, t.route));
  const tools = canUseMarketingTools(userRole) ? [MARKETING_TOOL, ...visible] : visible;
  return (
    <div style={styles.page}>
      <style>{CSS}</style>
      <div style={styles.wrap}>
        <button onClick={() => navigate('/dashboard')} style={styles.back}><ArrowLeft size={16} /> Back to dashboard</button>
        <div style={styles.hero}>
          <h1 style={styles.h1}>Tools</h1>
          <p style={styles.muted}>Estimates only. Change any number to match your deal, and confirm final figures with the title company or lender.</p>
        </div>
        <div style={styles.grid}>
          {tools.map(({ title, text, Icon, route, href, image }) => {
            const photoStyle = image
              ? { ...styles.photo, backgroundImage: `url(${image})`, backgroundSize: 'cover', backgroundPosition: 'center' }
              : styles.photo;
            return (
              <button key={title} className="tools-tile" type="button" onClick={() => (href ? window.open(href, '_blank', 'noopener,noreferrer') : navigate(route))} style={styles.tile}>
                <div style={photoStyle}>
                  {!image && (
                    <div style={styles.iconWrap}>
                      <Icon size={28} color="#D4AF37" />
                    </div>
                  )}
                </div>
                <div style={styles.body}>
                  <div style={styles.title}>{title}</div>
                  <p style={styles.text}>{text}</p>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
